import Header from './Header2';
import Popup from '../Component/CostLine/CostlineCreate';
import "../styles/grid.css";
import Vector from "../assets/svg/editIcon.svg";
import Delete from "../assets/svg/DeleteIcon.svg";
import { useState, useRef, useEffect } from "react";
import DatePicker from "../Component/DatePicker";
import { useNavigate } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import axiosInstance from '../axiosInstance';
import { ToastContainer, toast } from "react-toastify";
import { resetPeriods } from '../Redux/slices';
import { useDispatch } from "react-redux";
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import CommonFooter from '../CommonFooter/CommonFooter';
import { useRolePermission } from '../hooks/useRolePermission';

const ScopeGrid = () => {

  const checkPermission = useRolePermission();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const popupRef = useRef(null);
  const [limit, setLimit] = useState(5);
  const [skip, setSkip] = useState(0);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("dateCreated");
  const [reload, setReload] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = async (term = '') => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(
        `/costsolution/costlines`,
        {
          params: { limit, skip, sortBy, search: term || undefined, }
        }
      );

      const data = response.data;

      if (data && Array.isArray(data.costlines)) {
        setTableData(data.costlines);
        setTotal(data.totalCount || 0);
        setTotalPages(Math.ceil((data.totalCount || 0) / limit));
      } else {
        setTableData([]);
        setTotal(0);
      }
    } catch (error) {
      setError(error.message);
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and pagination changes
  useEffect(() => {
    fetchData(searchTerm);
  }, [skip, limit, reload, sortBy]);

  // Handle date selection
  const handleDateSelection = (date) => {
    console.log("Selected Date:", date);
    // You might want to add date filtering logic here
  };

  const handleAddNewClick = () => {
    dispatch(resetPeriods());
    navigate("/costLine_add_elements");
  };

  const closePopup = () => setIsPopupOpen(false);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        closePopup();
      }
    };

    if (isPopupOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPopupOpen]);

  // Selection handlers
  const isAllSelected = selectedRows.length === tableData.length;
  const isAnySelected = selectedRows.length > 0;

  const handleSelectAll = () => {
    setSelectedRows(isAllSelected ? [] : tableData.map((row) => row.id));
  };

  const handleRowSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      const response = await axiosInstance.delete(
        `/costsolution/costlines/delete/${deleteId}`
      );

      if (response.status === 200) {
        toast.success("Cost line deleted successfully!");
        setTableData((prev) => prev.filter((row) => row.id !== deleteId));
        setTotal((prev) => prev - 1);
        setReload(prev => !prev);
      } else {
        toast.error("Failed to delete the cost line!");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Something went wrong while deleting!");
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    setSkip(selected * limit);
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setLimit(newLimit);
    setCurrentPage(0);
    setSkip(0);
  };

  const handleEditClick = async (id) => {
    try {
      // First get the cost line details to extract the project ID
      const costlineRes = await axiosInstance.get(`/costsolution/costlines/get/${id}`);
      const projectId = costlineRes.data.costlines.projectId;

      console.log("costlineRes..........", costlineRes);

      // Now use the projectId for all subsequent calls
      const [periodsRes, categoriesRes, structureRes] = await Promise.all([
        axiosInstance.get(`/costsolution/costlines/getperiods/${projectId}`),
        axiosInstance.get(`/costsolution/costlines/getcategory/${projectId}`),
        axiosInstance.get(`/costsolution/costlinestructures/${id}`)
      ]);

      // Combine all responses
      const combinedData = {
        ...costlineRes.data.costlines,
        ProjectPeriod: periodsRes.data.ProjectPeriod,
        projectcategory: categoriesRes.data.projectcategory,
        periodid: categoriesRes.data.periodid,
        costlinestructure: structureRes.data.costlinestructure
      };

      console.log("data id", id)
      navigate(`/costLine_add_elements`, {
        state: {
          costline_data: combinedData,
          isEditMode: true,
          costlineId: id
        }
      });

    } catch (error) {
      console.error("Error fetching cost line data:", error);
      toast.error("Failed to load cost line data");
    }
  };

  const handleRowClick = async (id) => {
    try {
      const costlineRes = await axiosInstance.get(`/costsolution/costlines/get/${id}`);
      const projectId = costlineRes.data.costlines.projectId;

      const [periodsRes, categoriesRes, structureRes] = await Promise.all([
        axiosInstance.get(`/costsolution/costlines/getperiods/${projectId}`),
        axiosInstance.get(`/costsolution/costlines/getcategory/${projectId}`),
        axiosInstance.get(`/costsolution/costlinestructures/${id}`)
      ]);

      const combinedData = {
        ...costlineRes.data.costlines,
        ProjectPeriod: periodsRes.data.ProjectPeriod,
        projectcategory: categoriesRes.data.projectcategory,
        periodid: categoriesRes.data.periodid,
        costlinestructure: structureRes.data.costlinestructure
      };

      navigate(`/costLineviewpage`, {
        state: { costline_data: combinedData, isEditMode: true, costlineId: id }
      });

    } catch (error) {
      console.error("Error fetching cost line data:", error);
      toast.error("Failed to load cost line data");
    }
  };

  const handleSearch = async (term) => {
    setSearchTerm(term);
    setSkip(0);
    await fetchData(term);
  };



  return (
    <>
      <Header onSearch={handleSearch} />
      <div className="container">
        <div className="header">
          <div className="left-header">
            <h3>All Cost Lines ({total})</h3>
          </div>
          <div className="right-header">
            {/* {!isAnySelected ? (
              <>
                <DatePicker onDateChange={handleDateSelection} />
                <h4>Sort by :</h4>
                <div className="dropdown">
                  <select 
                    id="options" 
                    className="dropdown-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="dateCreated">Date Created</option>
                    <option value="name">Name</option>
                    <option value="code">Code</option>
                  </select>
                </div>
              </>
            ) : (
              <button 
                id="deleteBtn" 
                className="deleteBtn" 
                onClick={() => {
                  // Handle multiple deletions
                  selectedRows.forEach(id => handleDelete(id));
                  setSelectedRows([]);
                }}
              >
                Delete
              </button>
            )} */}

            {checkPermission("Cost Line", "create_permission") && !isAnySelected && (
              <button id="addNewBtn" onClick={handleAddNewClick}>
                Add New
              </button>
            )}

          </div>
        </div>


        <table>
          <thead>
            <tr>
              <th style={{ width: "1px" }}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                />
              </th>
              <th>S.No</th>
              <th>Project Name</th>
              <th>Model</th>
              <th>Modified Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData.map((row, index) => (
              <tr key={row.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(row.id)}
                    onChange={() => handleRowSelect(row.id)}
                  />
                </td>
                <td>{skip + index + 1}</td>
                <td style={{ cursor: 'pointer' }} onClick={() => handleRowClick(row.id)} >{row.projectName || '-'}</td>
                <td style={{ cursor: 'pointer' }} onClick={() => handleRowClick(row.id)} >{row.modelCode || '-'}</td>
                <td style={{ cursor: 'pointer' }} onClick={() => handleRowClick(row.id)} >{row.modifieddate || '-'}</td>

                <td>
                  <div className="actions">

                    {checkPermission("Cost Line", "edit_permission") && (
                      <span
                        className="edit-icon"
                        onClick={() => handleEditClick(row.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <img src={Vector} alt="Edit" width="20" height="20" />
                      </span>
                    )}

                    {checkPermission("Cost Line", "delete_permission") && (
                      <span
                        className="delete-icon"
                        onClick={() => handleDelete(row.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <img src={Delete} alt="Delete" width="40" height="40" />
                      </span>
                    )}

                  </div>
                </td>
                </tr>
              ))) : (
              <tr>
                {searchTerm && tableData.length === 0 && (
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px', fontSize: '14px', color: '#666' }}>
                    No Costline found matching "{searchTerm}"
                  </td>
                )}
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination controls */}
        <div className="pagination-container d-flex align-items-center justify-content-between mt-3">
          <div className="Limit-Dropdown">
            <label htmlFor="limit" className="fw-semibold">Items per page:</label>
            <select
              id="limit"
              value={limit}
              onChange={handleLimitChange}
              className="form-select d-inline w-auto"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
            </select>
          </div>

          <div className="d-flex align-items-center gap-4">
            <ReactPaginate
              previousLabel={<span style={{ userSelect: "none" }}>Previous</span>}
              nextLabel={<span style={{ userSelect: "none" }}>Next</span>}
              pageCount={totalPages > 0 ? totalPages : 1}
              onPageChange={handlePageClick}
              containerClassName={"pagination d-flex"}
              activeClassName={"active"}
              marginPagesDisplayed={1}
              pageRangeDisplayed={3}
              forcePage={currentPage}
            />
          </div>
        </div>
      </div>

      {isPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-content" ref={popupRef}>
            <Popup closePopup={closePopup} />
          </div>
        </div>
      )}
      <CommonFooter />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <ConfirmDialog
        open={confirmOpen}
        title="Delete Confirmation"
        message="Are you sure you want to delete this Costline?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};

export default ScopeGrid;